import React, { useState } from 'react';
import axios from 'axios';
import { QRCodeSVG } from 'qrcode.react';
import PdfUploadEditor from './PdfUploadEditor';
import './DocumentActions.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3002';

function DocumentActions({ 
  truckData, 
  customerData, 
  paymentData, 
  lienData, 
  insuranceData, 
  taxExemptData, 
  rd108Data 
}) {
  const [step, setStep] = useState('generate');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [generatedFiles, setGeneratedFiles] = useState(null);
  const [documentPath, setDocumentPath] = useState(null);
  const [driveFolderId, setDriveFolderId] = useState(null);
  const [saveAsDraft, setSaveAsDraft] = useState(false);
  const [signingResult, setSigningResult] = useState(null);
  const [linkCopied, setLinkCopied] = useState(false);

  // Additional documents state
  const [showPdfEditor, setShowPdfEditor] = useState(false);
  const [additionalDocuments, setAdditionalDocuments] = useState([]);

  const handleGenerateDocuments = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.post(`${API_URL}/api/generate-documents`, {
        truck: truckData,
        customer: customerData,
        payment: paymentData,
        lien: lienData,
        insurance: insuranceData,
        taxExempt: taxExemptData,
        rd108: rd108Data
      });
      setGeneratedFiles(response.data);
      setDocumentPath(response.data.documentPaths[0]);
      setDriveFolderId(response.data.folderId || null);
      setStep('review');
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to generate documents');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenPDF = async () => {
    setLoading(true);
    setError(null);
    try {
      await axios.post(`${API_URL}/api/open-pdf-preview`, {
        pdfPath: documentPath
      });
      setStep('review-open'); // New step — PDF is open, waiting for user to close
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to open PDF');
    } finally {
      setLoading(false);
    }
  };

  const handleFlattenAndUpload = async () => {
    setLoading(true);
    setError(null);
    try {
      await axios.post(`${API_URL}/api/flatten-and-upload`, {
        pdfPath: documentPath,
        folderId: driveFolderId,
        truck: truckData
      });
      setStep('additional-docs');
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to flatten and upload');
    } finally {
      setLoading(false);
    }
  };

  const handlePdfEditorComplete = (data) => {
    setAdditionalDocuments([...additionalDocuments, data]);
    setShowPdfEditor(false);
  };

  const removeAdditionalDocument = (index) => {
    setAdditionalDocuments(additionalDocuments.filter((_, i) => i !== index));
  };

  const handleSendForSignature = async () => {
    if (!customerData.email) {
      setError('Customer email is required for signing');
      return;
    }
    setLoading(true);
    setError(null);
    setSigningResult(null);
    try {
      const formData = new FormData();
      formData.append('pdfPath', documentPath);
      formData.append('documentType', 'all');
      formData.append('signerEmail', customerData.email);
      formData.append('signerName', customerData.contactName || customerData.companyName);
      formData.append('stockNumber', truckData.stockNumber);
      formData.append('sendMode', saveAsDraft ? 'draft' : 'send');
      additionalDocuments.forEach((doc) => {
        formData.append('additionalDocs', doc.file);
        formData.append('additionalDocsFields', JSON.stringify(doc.fields));
      });
      const response = await axios.post(`${API_URL}/api/send-for-signature`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 300000
      });
      setSigningResult(response.data);
      setStep('complete');
    } catch (err) {
      let errorMessage = 'Failed to send for signature';
      if (err.code === 'ECONNABORTED') errorMessage = 'Request timed out.';
      else if (err.response) errorMessage = err.response.data?.message || `Server error: ${err.response.status}`;
      else if (err.request) errorMessage = 'Cannot reach backend server on port 3002.';
      else errorMessage = err.message;
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(() => {
      setLinkCopied(true);
      setTimeout(() => setLinkCopied(false), 2000);
    });
  };

  const handleReset = () => {
    setStep('generate');
    setGeneratedFiles(null);
    setDocumentPath(null);
    setDriveFolderId(null);
    setSigningResult(null);
    setError(null);
    setAdditionalDocuments([]);
    setLinkCopied(false);
  };

  return (
    <section className="card document-actions">
      <h3>📄 Document Generation & Signing</h3>

      {error && (
        <div className="alert alert-error">
          <strong>⚠️</strong> {error}
        </div>
      )}

      {/* Generate */}
      {step === 'generate' && (
        <div className="action-step">
          <h4>Step 1: Generate Documents</h4>
          <p>Creates all required PDFs and uploads to Google Drive</p>
          <button onClick={handleGenerateDocuments} disabled={loading} className="btn-primary btn-large">
            {loading ? '⏳ Generating...' : '📄 Generate Documents'}
          </button>
        </div>
      )}

      {/* Review */}
      {step === 'review' && (
        <div className="action-step">
          <h4>✅ Documents Generated!</h4>
          <p>{generatedFiles?.files?.length || 0} files created in Google Drive</p>
          <h4>Step 2: Review Documents (Optional)</h4>
          <p>Open in Adobe Reader to review. When finished, close Adobe and click "Done Reviewing".</p>
          <button onClick={handleOpenPDF} disabled={loading} className="btn-primary">
            {loading ? '⏳ Opening...' : '📄 Open PDF in Adobe'}
          </button>
          <button onClick={() => setStep('additional-docs')} className="btn-secondary" style={{marginLeft: '10px'}}>
            Skip to Signing →
          </button>
        </div>
      )}

      {/* Review — PDF is open, waiting for user to close Adobe */}
      {step === 'review-open' && (
        <div className="action-step">
          <h4>📄 PDF is open in Adobe Reader</h4>
          <div style={{background:'#fff8e1', border:'2px solid #f59e0b', borderRadius:'8px', padding:'16px', marginBottom:'20px'}}>
            <p style={{margin:0, fontWeight:'600', color:'#92400e'}}>⚠️ Review your document in Adobe Reader</p>
            <p style={{margin:'8px 0 0 0', color:'#78350f', fontSize:'14px'}}>
              When you are finished reviewing, <strong>close Adobe Reader</strong> then click the button below.
            </p>
          </div>
          <button onClick={handleFlattenAndUpload} disabled={loading} className="btn-primary" style={{marginBottom:'10px', width:'100%'}}>
            {loading ? '⏳ Flattening & Uploading to Drive...' : '✅ Done Reviewing — Flatten & Upload to Drive'}
          </button>
          <button onClick={() => setStep('additional-docs')} className="btn-secondary" style={{width:'100%'}}>
            Skip — Proceed to Signing →
          </button>
        </div>
      )}

      {/* Additional Documents */}
      {step === 'additional-docs' && !showPdfEditor && (
        <div className="action-step">
          <h4>Step 3: Add Additional Documents (Optional)</h4>
          {additionalDocuments.length > 0 && (
            <div className="added-documents-list">
              <h5>Documents to Include:</h5>
              {additionalDocuments.map((doc, index) => (
                <div key={index} className="added-document-item">
                  <div className="doc-info">
                    <span className="doc-icon">📄</span>
                    <div>
                      <strong>{doc.file.name}</strong>
                      <div className="doc-fields">{doc.fields.length} signature field(s) placed</div>
                    </div>
                  </div>
                  <button onClick={() => removeAdditionalDocument(index)} className="btn-danger-small">Remove</button>
                </div>
              ))}
            </div>
          )}
          <button onClick={() => setShowPdfEditor(true)} className="btn-primary" style={{marginBottom: '15px'}}>
            + Add Document for Signing
          </button>
          <p className="step-hint">Add titles, lien releases, or other documents that need customer signatures.</p>
          <div className="step-navigation">
            <button onClick={() => setStep('review')} className="btn-secondary">← Back</button>
            <button onClick={() => setStep('sign')} className="btn-primary">Continue to Send →</button>
          </div>
        </div>
      )}

      {/* PDF Editor */}
      {step === 'additional-docs' && showPdfEditor && (
        <div style={{ position: 'relative', left: '50%', right: '50%', marginLeft: '-45vw', marginRight: '-45vw', width: '90vw' }}>
          <PdfUploadEditor onComplete={handlePdfEditorComplete} onCancel={() => setShowPdfEditor(false)} />
        </div>
      )}

      {/* Sign */}
      {step === 'sign' && (
        <div className="action-step">
          <h4>Step 4: Send for Electronic Signature</h4>
          <div style={{background: '#f8f9fa', padding: '15px', borderRadius: '8px', marginBottom: '15px'}}>
            <p><strong>Signer:</strong> {customerData.contactName}</p>
            <p><strong>Email:</strong> {customerData.email}</p>
            <p><strong>Stock:</strong> {truckData.stockNumber}</p>
            <div style={{marginTop: '10px', paddingTop: '10px', borderTop: '1px solid #ddd'}}>
              <strong>Documents included:</strong>
              <ul style={{marginTop: '5px', marginBottom: 0}}>
                <li>Base documents (Invoice, RD-108, As-Is, Tax Exemption, Title Release) - 11 fields</li>
                {additionalDocuments.map((doc, index) => (
                  <li key={index}>{doc.file.name} - {doc.fields.length} field(s)</li>
                ))}
              </ul>
              <p style={{marginTop: '10px', fontWeight: 'bold', color: '#4CAF50'}}>
                Total: {11 + additionalDocuments.reduce((sum, doc) => sum + doc.fields.length, 0)} signature fields
              </p>
            </div>
          </div>

          <div style={{marginBottom: '15px'}}>
            <label style={{display: 'flex', alignItems: 'flex-start', padding: '15px', background: '#f8f9fa', borderRadius: '8px', cursor: 'pointer'}}>
              <input
                type="checkbox"
                checked={saveAsDraft}
                onChange={(e) => setSaveAsDraft(e.target.checked)}
                disabled={loading}
                style={{marginRight: '10px', marginTop: '4px', width: '20px', height: '20px'}}
              />
              <div>
                <strong>Save as draft</strong>
                <div style={{fontSize: '14px', color: '#666', marginTop: '5px'}}>Creates document without sending.</div>
              </div>
            </label>
          </div>

          {loading && (
            <div style={{marginBottom: '15px', padding: '15px', background: '#e7f3ff', borderLeft: '4px solid #2196F3', borderRadius: '4px'}}>
              <strong>⏳ Processing document...</strong>
              <p style={{marginTop: '8px', marginBottom: 0, fontSize: '14px'}}>
                This takes 30-90 seconds. Opening PDF, flattening, uploading to Documenso...
              </p>
            </div>
          )}

          <button
            onClick={handleSendForSignature}
            disabled={loading || !customerData.email}
            className={`btn-large ${saveAsDraft ? 'btn-secondary' : 'btn-primary'}`}
            style={{marginBottom: '10px'}}
          >
            {loading ? '⏳ Processing (30-90 sec)...' : saveAsDraft ? '📋 Create Draft' : '✉️ Send for Signature'}
          </button>

          <button onClick={() => setStep('additional-docs')} className="btn-secondary" style={{width: '100%'}}>
            ← Back to Add Documents
          </button>
        </div>
      )}

      {/* Complete */}
      {step === 'complete' && signingResult && (
        <div className="action-step">
          <div className="alert alert-success">
            <h4>✅ {signingResult.message}</h4>

            {signingResult.mode === 'draft' ? (
              <>
                <p>Document created as draft in Documenso</p>
                <p><strong>Document ID:</strong> {signingResult.documentId}</p>
                <a
                  href="http://localhost:3001"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn btn-primary"
                  style={{marginTop: '10px', display: 'inline-block'}}
                >
                  Open in Documenso →
                </a>
              </>
            ) : (
              <>
                <p>Sent to: <strong>{customerData.email}</strong></p>
                <p><strong>Document ID:</strong> {signingResult.documentId}</p>
                {additionalDocuments.length > 0 && (
                  <p><strong>Includes:</strong> Base documents + {additionalDocuments.length} additional document(s)</p>
                )}

                {/* QR Code for in-person signing */}
                {signingResult.signingUrl ? (
                  <div style={{marginTop: '20px', textAlign: 'center'}}>
                    <h4 style={{marginBottom: '10px'}}>📱 In-Person Signing</h4>
                    <p style={{fontSize: '14px', color: '#555', marginBottom: '15px'}}>
                      Have the customer scan this QR code to sign on their phone or tablet:
                    </p>
                    <div style={{display: 'inline-block', padding: '16px', background: 'white', border: '2px solid #ddd', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)'}}>
                      <QRCodeSVG
                        value={signingResult.signingUrl}
                        size={220}
                        level="H"
                        includeMargin={false}
                      />
                    </div>
                    <div style={{marginTop: '15px'}}>
                      <button
                        onClick={() => copyToClipboard(signingResult.signingUrl)}
                        className="btn-secondary"
                        style={{marginRight: '10px'}}
                      >
                        {linkCopied ? '✅ Copied!' : '📋 Copy Signing Link'}
                      </button>
                    </div>
                    <p style={{fontSize: '12px', color: '#888', marginTop: '10px'}}>
                      Or share this link:{' '}
                      <a
                        href={signingResult.signingUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{wordBreak: 'break-all', color: '#1a73e8', textDecoration: 'underline'}}
                      >
                        {signingResult.signingUrl}
                      </a>
                    </p>
                  </div>
                ) : (
                  <p style={{marginTop: '10px', color: '#888', fontSize: '14px'}}>
                    Signing link unavailable. Customer will receive email with signing instructions.
                  </p>
                )}
              </>
            )}
          </div>

          <button onClick={handleReset} className="btn-primary" style={{marginTop: '15px', width: '100%'}}>
            ✨ Process Another Sale
          </button>
        </div>
      )}
    </section>
  );
}

export default DocumentActions;
